/**
 * @fileoverview State, effects, and handlers for the company profile Corporate Identity section.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  getOperationEnvironmentOptions,
  getCompanySizeOptions,
} from "../../../data/selectOptions";
import { getCompanyFieldValue } from "../../../utils/common";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import { useCorporateIdentityLogo } from "./useCorporateIdentityLogo";
import { useCorporateIdentityReferenceData } from "./useCorporateIdentityReferenceData";

function useCorporateIdentityValidation({
  hasErrors,
  error,
  onValidationChange,
}) {
  const prevHasErrorsRef = useRef(false);

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    if (onValidationChange) {
      // Include both field validation errors and file validation errors
      const hasFileErrors = !!error;
      const hasAnyErrors = hasErrors || hasFileErrors;

      if (prevHasErrorsRef.current !== hasAnyErrors) {
        prevHasErrorsRef.current = hasAnyErrors;
        onValidationChange(hasAnyErrors);
      }
    }
  }, [hasErrors, error, onValidationChange]);
}

export function useCorporateIdentitySection({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMediumMobile = window.innerWidth >= 481 && window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth >= 320 && window.innerWidth <= 480;
  const isMobile = isMediumMobile || isSmallMobile;

  const [razonSocial, setRazonSocial] = useState(company?.razonSocial || "");

  const {
    fieldValues,
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    nombreComercial: company?.nombreComercial || "",
  });

  const [ambitoOperaciones, setAmbitoOperaciones] = useState(
    getCompanyFieldValue(company, "ambitoOperaciones", selectedProfile, null),
  );
  const [tamanoDeLaEmpresa, setTamanoDeLaEmpresa] = useState(
    company?.tamanoDeLaEmpresa || null,
  );
  const [ramaSCIAN, setRamaSCIAN] = useState(company?.ramaSCIAN ?? null);
  const [sectorEconomico, setSectorEconomico] = useState(
    company?.sectorEconomico ?? null,
  );
  const [subSectorSCIAN, setSubSectorSCIAN] = useState(
    company?.subSectorSCIAN ?? null,
  );
  const [grupoEmpresarial, setGrupoEmpresarial] = useState(
    company?.grupoEmpresarial || null,
  );

  const logo = useCorporateIdentityLogo({ company, onFieldChange, t });
  const referenceData = useCorporateIdentityReferenceData({
    navigate,
    sectorEconomico,
    subSectorSCIAN,
    ramaSCIAN,
  });

  useEffect(() => {
    if (company?.sectorEconomico !== undefined) {
      setSectorEconomico(company.sectorEconomico);
    }
    if (company?.subSectorSCIAN !== undefined) {
      setSubSectorSCIAN(company.subSectorSCIAN);
    }
    if (company?.ramaSCIAN !== undefined) {
      setRamaSCIAN(company.ramaSCIAN);
    }
  }, [company?.sectorEconomico, company?.subSectorSCIAN, company?.ramaSCIAN]);

  useEffect(() => {
    if (company?.nombreComercial !== undefined) {
      handleFieldChange("nombreComercial", company.nombreComercial);
    }
    if (company?.razonSocial !== undefined) {
      setRazonSocial(company.razonSocial);
    }
    // Use profile-aware value for ambitoOperaciones
    const ambitoValue = getCompanyFieldValue(
      company,
      "ambitoOperaciones",
      selectedProfile,
      null,
    );
    setAmbitoOperaciones(ambitoValue);

    if (company?.tamanoDeLaEmpresa !== undefined) {
      setTamanoDeLaEmpresa(company.tamanoDeLaEmpresa);
    }
  }, [
    company?.nombreComercial,
    company?.razonSocial,
    company?.ambitoOperaciones,
    company?.tamanoDeLaEmpresa,
    selectedProfile,
    company?.perfiles,
  ]);

  useCorporateIdentityValidation({
    hasErrors,
    error: logo.error,
    onValidationChange,
  });

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  // Handle validated field changes
  const handleValidatedFieldChange = (fieldName, value) => {
    handleFieldChange(fieldName, value);
    handleInputChange(fieldName, value);
  };

  const operationEnvironmentOptions = getOperationEnvironmentOptions(t);
  const companySizeOptions = getCompanySizeOptions(t);

  return {
    t,
    isMobile,
    razonSocial,
    setRazonSocial,
    fieldValues,
    getErrorMessage,
    getFieldValidationState,
    ambitoOperaciones,
    setAmbitoOperaciones,
    tamanoDeLaEmpresa,
    setTamanoDeLaEmpresa,
    ramaSCIAN,
    setRamaSCIAN,
    sectorEconomico,
    setSectorEconomico,
    subSectorSCIAN,
    setSubSectorSCIAN,
    grupoEmpresarial,
    setGrupoEmpresarial,
    operationEnvironmentOptions,
    companySizeOptions,
    handleInputChange,
    handleValidatedFieldChange,
    ...logo,
    ...referenceData,
  };
}
